/**
 * Feedback widget (WS8-E): a "Send feedback" button + modal, dropped into
 * every authenticated layout's sidebar.
 *
 * The context block (page, org, role, browser) is shown to the user before
 * sending — nothing is attached invisibly.
 */

import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import Modal from '../ui/Modal';
import { submitFeedback, type FeedbackCategory } from '../../api/feedback';
import { getErrorMessage } from '../../api/client';
import { useOrganization } from '../../context/OrganizationContext';

const CATEGORIES: { value: FeedbackCategory; label: string }[] = [
  { value: 'BUG', label: 'Bug: something is broken' },
  { value: 'IDEA', label: 'Idea: a suggestion or request' },
  { value: 'QUESTION', label: 'Question' },
  { value: 'OTHER', label: 'Other' },
];

interface FeedbackWidgetProps {
  /** Extra classes for the trigger button, to match each layout's nav styling. */
  buttonClassName?: string;
}

export default function FeedbackWidget({ buttonClassName = '' }: FeedbackWidgetProps) {
  const location = useLocation();
  const { currentOrg } = useOrganization();
  const [isOpen, setIsOpen] = useState(false);
  const [category, setCategory] = useState<FeedbackCategory>('IDEA');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const pageUrl = `${window.location.origin}${location.pathname}`;

  const close = () => {
    if (!sending) setIsOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim().length < 5) {
      toast.error('Please write at least a few words.');
      return;
    }
    setSending(true);
    try {
      await submitFeedback({
        category,
        message: message.trim(),
        pageUrl,
        userAgent: navigator.userAgent,
        orgSlug: currentOrg?.orgSlug,
        orgRole: currentOrg?.role,
      });
      toast.success('Thanks, your feedback has been sent.');
      setIsOpen(false);
      setMessage('');
      setCategory('IDEA');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button type="button" onClick={() => setIsOpen(true)} className={buttonClassName}>
        Send feedback
      </button>

      <Modal isOpen={isOpen} onClose={close} size="lg" labelledBy="feedback-modal-title">
        <form onSubmit={handleSubmit} className="p-6">
          <h2 id="feedback-modal-title" className="text-lg font-semibold text-gray-900 mb-4">
            Send feedback
          </h2>

          <div className="mb-4">
            <label htmlFor="feedback-category" className="block text-sm font-medium text-gray-700 mb-1">
              What kind of feedback?
            </label>
            <select
              id="feedback-category"
              value={category}
              onChange={(e) => setCategory(e.target.value as FeedbackCategory)}
              className="block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-rack focus:border-rack"
            >
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <label htmlFor="feedback-message" className="block text-sm font-medium text-gray-700 mb-1">
              Your message
            </label>
            <textarea
              id="feedback-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              maxLength={5000}
              autoFocus
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-rack focus:border-rack"
              placeholder="What happened, or what would make ProgramRow better?"
            />
          </div>

          {/* Context the submission carries — visible, not hidden */}
          <div className="mb-5 rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-xs text-gray-500 space-y-0.5">
            <p className="font-medium text-gray-600">Sent along with your message:</p>
            <p>Page: {pageUrl}</p>
            {currentOrg && (
              <p>
                Organization: {currentOrg.orgName} ({currentOrg.role})
              </p>
            )}
            <p className="truncate">Browser: {navigator.userAgent}</p>
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={close}
              disabled={sending}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={sending || message.trim().length < 5}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? 'Sending…' : 'Send feedback'}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}
